import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UserHealth, UserHealthSignal } from '../../services/adminService';
import './UserHealthCard.css';

type Props = {
  health: UserHealth | null;
  loading?: boolean;
};

const formatLearningTime = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
};

const formatTokens = (n: number): string => n.toLocaleString();

export const UserHealthCard: React.FC<Props> = ({ health, loading }) => {
  const { t } = useTranslation('admin');

  if (loading || !health) {
    return (
      <div className="user-health-card user-health-card--loading">
        <div className="user-health-card__skeleton" />
      </div>
    );
  }

  const statusLabels: Record<UserHealth['activityStatus'], { label: string; cls: string }> = {
    power_user: { label: t('userDetail.health.statusPowerUser', 'Power User'), cls: 'user-health-card__status--power' },
    active: { label: t('userDetail.health.statusActive', 'Active'), cls: 'user-health-card__status--active' },
    at_risk: { label: t('userDetail.health.statusAtRisk', 'At Risk'), cls: 'user-health-card__status--atrisk' },
    dormant: { label: t('userDetail.health.statusDormant', 'Dormant'), cls: 'user-health-card__status--dormant' },
    churned: { label: t('userDetail.health.statusChurned', 'Churned'), cls: 'user-health-card__status--churned' },
    never_active: { label: t('userDetail.health.statusNeverActive', 'Never Active'), cls: 'user-health-card__status--never' },
  };
  const status = statusLabels[health.activityStatus];

  const trendPct = health.learningTimeTrendPct;
  const trendUp = trendPct !== null && trendPct > 0;
  const trendDown = trendPct !== null && trendPct < 0;
  const trendCls = trendUp ? 'up' : trendDown ? 'down' : 'flat';

  const subStateLabel = (() => {
    switch (health.subscriptionState) {
      case 'trialing': return t('userDetail.health.subTrialing', 'Trial');
      case 'active': return t('userDetail.health.subActive', 'Active');
      case 'cancelled': return t('userDetail.health.subCancelled', 'Cancelled');
      case 'expired': return t('userDetail.health.subExpired', 'Expired');
      case 'never': return t('userDetail.health.subNever', 'Never subscribed');
    }
  })();

  return (
    <div className="user-health-card">
      <div className="user-health-card__header">
        <span className={`user-health-card__status ${status.cls}`}>
          <span className="user-health-card__status-dot" />
          {status.label}
        </span>
        <span className="user-health-card__active-summary">
          {t('userDetail.health.activeSummary', '{{days}} active days in last 7', { days: health.activeDays7d })}
          {' · '}
          {t('userDetail.health.active30d', '{{days}} of last 30', { days: health.activeDays30d })}
        </span>
      </div>

      {health.signals.length > 0 && (
        <div className="user-health-card__signals">
          {health.signals.map((s, i) => (
            <SignalChip key={`${s.type}-${i}`} signal={s} />
          ))}
        </div>
      )}

      <div className="user-health-card__kpis">
        <div className="user-health-card__kpi">
          <div className="user-health-card__kpi-label">
            {t('userDetail.health.kpiLearning7d', 'Learning (7d)')}
          </div>
          <div className="user-health-card__kpi-value">
            {formatLearningTime(health.learningTime7d)}
          </div>
          {trendPct !== null && (
            <div className={`user-health-card__kpi-trend user-health-card__kpi-trend--${trendCls}`}>
              {trendUp && '▲'}
              {trendDown && '▼'}
              {!trendUp && !trendDown && '—'}
              {' '}
              {trendPct > 0 ? '+' : ''}{trendPct}%
              {' '}
              <span className="user-health-card__kpi-trend-note">
                {t('userDetail.health.vsPrev7d', 'vs prev 7d')}
              </span>
            </div>
          )}
          {trendPct === null && health.learningTime7d === 0 && (
            <div className="user-health-card__kpi-trend user-health-card__kpi-trend--flat">
              {t('userDetail.health.noActivity', 'No activity')}
            </div>
          )}
        </div>

        <div className="user-health-card__kpi">
          <div className="user-health-card__kpi-label">
            {t('userDetail.health.kpiSubscription', 'Subscription')}
          </div>
          <div className="user-health-card__kpi-value">{subStateLabel}</div>
          {health.subscriptionDaysRemaining !== null && (
            <div className="user-health-card__kpi-trend user-health-card__kpi-trend--flat">
              {t('userDetail.health.daysRemaining', '{{days}} days remaining', {
                days: health.subscriptionDaysRemaining,
              })}
            </div>
          )}
        </div>

        <div className="user-health-card__kpi">
          <div className="user-health-card__kpi-label">
            {t('userDetail.health.kpiTokens', 'Tokens')}
          </div>
          <div className="user-health-card__kpi-value">{formatTokens(health.tokenBalance)}</div>
          <div className="user-health-card__kpi-trend user-health-card__kpi-trend--flat">
            {t('userDetail.health.tokensSpent7d', '{{spent}} spent in 7d', {
              spent: formatTokens(health.tokenSpent7d),
            })}
            {health.tokenSpent7dAvg > 0 && (
              <span className="user-health-card__kpi-trend-note">
                {' · '}
                {t('userDetail.health.avgPerWeek', 'avg {{avg}}/wk', {
                  avg: formatTokens(health.tokenSpent7dAvg),
                })}
              </span>
            )}
          </div>
        </div>

        <div className="user-health-card__kpi">
          <div className="user-health-card__kpi-label">
            {t('userDetail.health.kpiActiveDays30', 'Active days (30d)')}
          </div>
          <div className="user-health-card__kpi-value">
            {health.activeDays30d}
            <span className="user-health-card__kpi-value-unit">/30</span>
          </div>
          <div className="user-health-card__kpi-trend user-health-card__kpi-trend--flat">
            {health.activeDays90d > 0
              ? t('userDetail.health.activeDays90', '{{days}} of last 90', { days: health.activeDays90d })
              : t('userDetail.health.noRecent90d', 'No activity in 90d')}
          </div>
        </div>
      </div>
    </div>
  );
};

const SignalChip: React.FC<{ signal: UserHealthSignal }> = ({ signal }) => {
  const { t } = useTranslation('admin');

  const message = (() => {
    switch (signal.type) {
      case 'suspended':
        return t('userDetail.health.signalSuspended', 'Account suspended');
      case 'email_unverified':
        return t('userDetail.health.signalEmailUnverified', 'Email not verified');
      case 'failed_payment':
        return t('userDetail.health.signalFailedPayment', '{{count}} failed payment(s) in 30d', {
          count: signal.data?.count ?? 0,
        });
      case 'trial_ending':
        return t('userDetail.health.signalTrialEnding', 'Trial ends in {{days}} day(s)', {
          days: signal.data?.days ?? 0,
        });
      case 'cancelled_soon':
        return t('userDetail.health.signalCancelledSoon', 'Subscription expires in {{days}} day(s)', {
          days: signal.data?.days ?? 0,
        });
      case 'no_engagement':
        return t('userDetail.health.signalNoEngagement', 'No activity in 7 days (was active recently)');
      case 'token_anomaly':
        return t('userDetail.health.signalTokenAnomaly', 'Token spending {{spent}} (3× avg {{avg}})', {
          spent: signal.data?.spent ?? 0,
          avg: signal.data?.avg ?? 0,
        });
    }
  })();

  return (
    <span className={`user-health-card__signal user-health-card__signal--${signal.severity}`}>
      <SignalIcon severity={signal.severity} />
      {message}
    </span>
  );
};

const SignalIcon: React.FC<{ severity: 'info' | 'warning' | 'danger' }> = ({ severity }) => {
  if (severity === 'danger') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    );
  }
  if (severity === 'warning') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
};
